import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffectOnce } from 'react-use';

export type QueryParams = Record<
  string,
  string | number | string[] | Date | undefined | null
>;

export const useUrlParams = (initialParams?: QueryParams) => {
  /**
   * Hooks
   */
  const navigate = useNavigate();
  const location = useLocation();

  // Remove empty / null / undefined params
  const cleanParams = useCallback((params: URLSearchParams) => {
    return new URLSearchParams(
      Array.from(params).filter(([, value]) => !!value)
    );
  }, []);

  // https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
  const queryParams = useMemo(() => {
    return cleanParams(new URLSearchParams(location.search));
  }, [cleanParams, location.search]);

  const applyParams = useCallback(
    (newParams: QueryParams, callBack?: (params: URLSearchParams) => void) => {
      const nextParams = new URLSearchParams(queryParams);

      for (const [key, val] of Object.entries(newParams)) {
        if (val === undefined || val === null) {
          nextParams.delete(key);
        } else if (Array.isArray(val)) {
          // TODO(Benoit): Support native HTML URL arrays instead? E.g. &foo=1&foo=2&...
          nextParams.set(key, val.join(','));
        } else {
          // string | number | Date
          nextParams.set(key, val.toString());
        }
      }

      navigate(`${location.pathname}?${nextParams.toString()}`, {
        replace: true,
      });

      callBack?.(nextParams);
    },
    [queryParams, navigate, location.pathname]
  );

  useEffectOnce(() => {
    if (initialParams) {
      applyParams(initialParams);
    }
  });

  return { applyParams, queryParams };
};
